// Una hoja para las listas del Chat (Historial, Memoria, Consumo) —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Es la misma anatomía que las hojas del asistente: en mobile, bottom sheet que sube
// desde abajo; en desktop, un panel flotante a la derecha. Va por portal a `document.body`
// y por encima del panel del chat (`z-[110]` en mobile): un overlay montado adentro del
// panel quedaría recortado por su `overflow`.

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  abierta: boolean;
  titulo: string;
  bajada?: string;
  onCerrar: () => void;
  children: React.ReactNode;
  /**
   * Sin el fondo oscuro que cierra al tocar: la hoja de Mezclar del modo Diseño necesita
   * que el lienzo siga tocable (sumar partes es tocar celdas). Escape sigue cerrando.
   */
  sinFondo?: boolean;
  /**
   * Un PIE fijo debajo del contenido que scrollea (los botones de Generar / Agregar de
   * las hojas del modo Diseño): siempre a la vista, sin scrollear hasta el fondo. Sin él
   * la hoja es la de siempre.
   */
  pie?: React.ReactNode;
}

export const HojaLateral: React.FC<Props> = ({ abierta, titulo, bajada, onCerrar, children, sinFondo, pie }) => {
  useEffect(() => {
    if (!abierta) return;
    const alTeclado = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [abierta, onCerrar]);

  if (!abierta) return null;

  const cabecera = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-[16px] font-semibold text-brand-black/90">{titulo}</h3>
        {bajada && (
          <p className="mt-0.5 text-[12.5px] leading-snug text-[#3C3C43]/55">{bajada}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 transition-colors hover:bg-black/[0.1]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  return createPortal(
    <>
      {!sinFondo && <div className="fixed inset-0 z-[130] bg-black/40 animate-fade-in" onClick={onCerrar} />}
      {/* Mobile: bottom sheet. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="fixed inset-x-0 bottom-0 z-[131] flex flex-col rounded-t-[20px] bg-white shadow-2xl animate-slide-up-sheet md:hidden"
        style={{
          maxHeight: '80dvh',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        }}
      >
        <div className="shrink-0 py-2">
          <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.15]" />
        </div>
        <div className="shrink-0 px-4 pb-2">{cabecera}</div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">{children}</div>
        {pie && <div className="shrink-0 border-t border-black/[0.08] px-4 pt-3">{pie}</div>}
      </div>
      {/* Desktop: panel flotante a la derecha. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="fixed bottom-4 right-4 top-4 z-[131] hidden w-[420px] flex-col overflow-hidden rounded-[13px] bg-white shadow-2xl md:flex"
      >
        <div className="shrink-0 px-5 pb-3 pt-4">{cabecera}</div>
        <div className="mx-5 h-px shrink-0 bg-black/[0.08]" />
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">{children}</div>
        {pie && <div className="shrink-0 border-t border-black/[0.08] px-5 py-4">{pie}</div>}
      </div>
    </>,
    document.body,
  );
};

export default HojaLateral;
